import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Shield, Mail, Lock, User, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login, register, setupMFA, verifyMFA, requiresMFA, mfaSetupRequired, resendMFA, user } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('auth'); // auth, mfa-setup, mfa-verify
  const [mfaType, setMfaType] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone_number: '',
    mfaCode: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    
    if (!isLogin && !agreedToTerms) {
      toast.error('Please agree to the Terms of Service and Privacy Policy');
      return;
    }
    
    setLoading(true);
    
    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (result.requires_mfa) {
          setStep('mfa-verify');
          setMfaType(result.user.mfa_type);
          toast.info('Please enter your MFA code');
        } else if (result.mfa_setup_required) {
          setStep('mfa-setup');
          toast.info('Please set up MFA for secure access');
        } else {
          toast.success('Welcome back!');
        }
      } else {
        const result = await register({
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          phone_number: formData.phone_number || null
        });
        setStep('mfa-setup');
        toast.success('Account created! Now set up MFA');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMFASetup = async (type) => {
    setLoading(true);
    setMfaType(type);
    
    try {
      const result = await setupMFA(type, formData.phone_number || null);
      
      if (type === 'authenticator') {
        setQrCode(result.qr_code);
      }
      
      setStep('mfa-verify');
      toast.success(result.message || 'MFA setup initiated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'MFA setup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMFAVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await verifyMFA(formData.mfaCode, mfaType);
      toast.success('MFA verified successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid MFA code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendMFA = async () => {
    try {
      await resendMFA();
      toast.success('Verification code resent');
    } catch (error) {
      toast.error('Failed to resend code');
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1762430047160-db0e2670c533?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A091E]/90 via-[#1F1B71]/80 to-[#4B167A]/70" />
        <div className="relative z-10 flex flex-col justify-center px-16">
          <div className="animate-fade-in">
            <h1 className="text-5xl font-bold mb-4 tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              SRM <span className="shimmer">Cadence</span>
            </h1>
            <p className="text-xl text-white/70 mb-8 max-w-md" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Enterprise Workforce Management Ecosystem
            </p>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-white font-semibold">Zero-Trust Security</p>
                <p className="text-white/50 text-sm">AES-256 Encryption</p>
              </div>
            </div>
            <div className="flex gap-2">
              {['ISO', 'GDPR', 'SOC2'].map((badge) => (
                <span key={badge} className="px-3 py-1 rounded-full text-xs font-medium bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30">
                  {badge}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#0A091E]">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
              SRM <span className="shimmer">Cadence</span>
            </h1>
          </div>

          {step === 'auth' && (
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {isLogin ? 'Welcome Back' : 'Create Account'}
                </h2>
                <p className="text-white/50">
                  {isLogin ? 'Enter your credentials to access your account' : 'Set up your secure account'}
                </p>
              </div>

              <form onSubmit={handleAuth} className="space-y-5">
                {!isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="full_name" className="text-white/70">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                      <Input
                        id="full_name"
                        name="full_name"
                        type="text"
                        placeholder="John Doe"
                        value={formData.full_name}
                        onChange={handleInputChange}
                        className="pl-10 bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                        data-testid="input-fullname"
                        required={!isLogin}
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white/70">Corporate Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="pl-10 bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      data-testid="input-email"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white/70">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="pl-10 pr-10 bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      data-testid="input-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/50"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {!isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="phone_number" className="text-white/70">Phone Number (for MFA & Notifications)</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                      <Input
                        id="phone_number"
                        name="phone_number"
                        type="tel"
                        placeholder="+1 (555) 000-0000"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                        className="pl-10 bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                        data-testid="input-phone"
                      />
                    </div>
                    <p className="text-white/40 text-xs">Required for SMS/Voice MFA. Standard message rates may apply.</p>
                  </div>
                )}

                {!isLogin && (
                  <div className="flex items-start space-x-3 p-4 rounded-lg bg-[#1F1B71]/20 border border-white/10">
                    <Checkbox
                      id="terms"
                      checked={agreedToTerms}
                      onCheckedChange={setAgreedToTerms}
                      className="mt-0.5 border-[#D4AF37]/50 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                      data-testid="checkbox-terms"
                    />
                    <label htmlFor="terms" className="text-sm text-white/70 leading-relaxed cursor-pointer">
                      I agree to the{' '}
                      <Link to="/terms" className="text-[#D4AF37] hover:underline" target="_blank">
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy" className="text-[#D4AF37] hover:underline" target="_blank">
                        Privacy Policy
                      </Link>
                      , including consent to receive SMS messages and automated voice calls for account verification and notifications. 
                      Message frequency varies. Reply STOP to opt-out.
                    </label>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading || (!isLogin && !agreedToTerms)}
                  className="w-full bg-[#D4AF37] text-[#0A091E] font-bold py-3 rounded-full hover:shadow-[0_0_25px_rgba(212,175,55,0.6)] shadow-[0_0_15px_rgba(212,175,55,0.4)] disabled:opacity-50"
                  data-testid="btn-submit-auth"
                >
                  {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Create Account'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-[#D4AF37] hover:text-[#D4AF37]/80 text-sm"
                  data-testid="btn-toggle-auth"
                >
                  {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
                </button>
              </div>

              {/* Legal Links Footer */}
              <div className="mt-8 pt-6 border-t border-white/10 text-center">
                <div className="flex justify-center gap-4 text-xs text-white/40">
                  <Link to="/terms" className="hover:text-[#D4AF37]">Terms of Service</Link>
                  <span>•</span>
                  <Link to="/privacy" className="hover:text-[#D4AF37]">Privacy Policy</Link>
                </div>
                <p className="mt-2 text-xs text-white/30">
                  © 2026 SRM Cadence. All rights reserved.
                </p>
              </div>
            </>
          )}

          {step === 'mfa-setup' && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Set Up MFA
                </h2>
                <p className="text-white/50">
                  Choose your preferred multi-factor authentication method
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => handleMFASetup('authenticator')}
                  disabled={loading}
                  className="w-full p-4 rounded-xl border border-white/10 hover:border-[#D4AF37]/50 bg-[#1F1B71]/20 flex items-center gap-4 group"
                  data-testid="btn-mfa-authenticator"
                >
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20">
                    <Shield className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-white">Authenticator App</p>
                    <p className="text-sm text-white/50">Google Authenticator, Authy, etc.</p>
                  </div>
                </button>

                <button
                  onClick={() => handleMFASetup('sms')}
                  disabled={loading || !formData.phone_number}
                  className="w-full p-4 rounded-xl border border-white/10 hover:border-[#D4AF37]/50 bg-[#1F1B71]/20 flex items-center gap-4 group disabled:opacity-50"
                  data-testid="btn-mfa-sms"
                >
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20">
                    <Phone className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-white">SMS Verification</p>
                    <p className="text-sm text-white/50">Receive code via text message</p>
                  </div>
                </button>

                <button
                  onClick={() => handleMFASetup('voice')}
                  disabled={loading || !formData.phone_number}
                  className="w-full p-4 rounded-xl border border-white/10 hover:border-[#D4AF37]/50 bg-[#1F1B71]/20 flex items-center gap-4 group disabled:opacity-50"
                  data-testid="btn-mfa-voice"
                >
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20">
                    <Phone className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-white">Voice Call</p>
                    <p className="text-sm text-white/50">Receive code via automated call</p>
                  </div>
                </button>
              </div>

              {!formData.phone_number && (
                <div className="space-y-2 mt-4">
                  <Label htmlFor="setup_phone" className="text-white/70">Phone Number (for SMS/Voice)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                    <Input
                      id="setup_phone"
                      name="phone_number"
                      type="tel"
                      placeholder="+1 (555) 000-0000"
                      value={formData.phone_number}
                      onChange={handleInputChange}
                      className="pl-10 bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 'mfa-verify' && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Verify MFA
                </h2>
                <p className="text-white/50">
                  {mfaType === 'authenticator' 
                    ? 'Enter the code from your authenticator app' 
                    : `Enter the code sent to your phone`}
                </p>
              </div>

              {qrCode && mfaType === 'authenticator' && (
                <div className="flex flex-col items-center mb-6">
                  <div className="p-4 bg-white rounded-xl mb-4">
                    <img src={qrCode} alt="QR Code" className="w-48 h-48" />
                  </div>
                  <p className="text-white/50 text-sm text-center">
                    Scan this QR code with your authenticator app
                  </p>
                </div>
              )}

              <form onSubmit={handleMFAVerify} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="mfaCode" className="text-white/70">Verification Code</Label>
                  <Input
                    id="mfaCode"
                    name="mfaCode"
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={formData.mfaCode}
                    onChange={handleInputChange}
                    className="text-center text-2xl tracking-widest bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37] font-mono"
                    data-testid="input-mfa-code"
                    maxLength={6}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || formData.mfaCode.length < 6}
                  className="w-full bg-[#D4AF37] text-[#0A091E] font-bold py-3 rounded-full hover:shadow-[0_0_25px_rgba(212,175,55,0.6)] shadow-[0_0_15px_rgba(212,175,55,0.4)]"
                  data-testid="btn-verify-mfa"
                >
                  {loading ? 'Verifying...' : 'Verify & Continue'}
                </Button>
              </form>

              {mfaType !== 'authenticator' && (
                <div className="text-center">
                  <button
                    onClick={handleResendMFA}
                    className="text-[#D4AF37] hover:text-[#D4AF37]/80 text-sm"
                    data-testid="btn-resend-mfa"
                  >
                    Didn't receive code? Resend
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
